import React, {ChangeEvent, useEffect, useState} from "react";
import {Box, Grid, IconButton, InputAdornment, TextField} from "@mui/material";
import {Visibility, VisibilityOff} from "@mui/icons-material";
import styled from "styled-components";
import axios from "axios";
import {parseError} from "../../services/http.service";
import {useNavigate} from "react-router-dom";
import {useSnackbar} from "../../contexts/SnackbarContext";
import {ActionButton, CancelButton} from "../common/ActionButtons";
import { validatePassword as validatePasswordUtil } from "../../utils/password-validation";
import {ButtonGroup} from "../../pages/user-settings/UserSettings.styled";
import {useTranslation} from 'react-i18next';
import FieldHelpSuffix from "../common/FieldHelpSuffix";

const StyledTextField = styled(TextField)`
    width: 100%;
    max-width: 550px;
`;

const PasswordSecurity: React.FC = () => {
    const {t} = useTranslation();
    const [currentPassword, setCurrentPassword] = useState<string>("");
    const [newPassword, setNewPassword] = useState<string>("");
    const [confirmPassword, setConfirmPassword] = useState<string>("");
    const [showCurrentPassword, setShowCurrentPassword] = useState<boolean>(false);
    const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
    const [currentPasswordError, setCurrentPasswordError] = useState<string>("");
    const [newPasswordError, setNewPasswordError] = useState<string>("");
    const [confirmPasswordError, setConfirmPasswordError] = useState<string>("");
    const [isFormChanged, setIsFormChanged] = useState<boolean>(false);
    const navigate = useNavigate();
    const {showSnackbar} = useSnackbar();

    useEffect(() => {
        const passwordChanged = currentPassword !== "" &&
            newPassword !== "" &&
            confirmPassword !== "" &&
            newPassword === confirmPassword;

        setIsFormChanged(passwordChanged);
    }, [currentPassword, newPassword, confirmPassword]);


    const handleSaveChanges = async () => {
        setCurrentPasswordError("");
        setNewPasswordError("");
        setConfirmPasswordError("");

        let hasErrors = false;

        if (newPassword && !currentPassword) {
            setCurrentPasswordError(t('passwordSecurity.currentPasswordRequired') as string);
            hasErrors = true;
        }

        if (newPassword !== confirmPassword && newPassword !== "") {
            setConfirmPasswordError(t('passwordSecurity.passwordsNotMatch') as string);
            hasErrors = true;
        }

        if (newPasswordError) {
            hasErrors = true;
        }

        if (hasErrors) {
            return;
        }

        try {
            if (currentPassword && newPassword && confirmPassword && newPassword === confirmPassword) {
                try {
                    await axios.patch("/auth/change-password", {
                        currentPassword,
                        newPassword,
                    });

                    showSnackbar(t('passwordSecurity.passwordChanged'), "success");

                    setCurrentPassword("");
                    setNewPassword("");
                    setConfirmPassword("");
                    setIsFormChanged(false);
                } catch (passwordError: any) {
                    const parsedError = parseError(passwordError);
                    showSnackbar(parsedError, "error");
                    if (passwordError.response && passwordError.response.status === 400) {
                        if (passwordError.response.data && passwordError.response.data.message) {
                            const errorMessage = passwordError.response.data.message;
                            if (errorMessage.includes("current password")) {
                                setCurrentPasswordError(t('passwordSecurity.invalidCurrentPassword') as string);
                            } else if (errorMessage.includes("new password")) {
                                setNewPasswordError(t('passwordSecurity.invalidNewPassword') as string);
                            } else {
                                setCurrentPasswordError(" ");
                                setNewPasswordError(" ");
                                setConfirmPasswordError(" ");
                            }
                        } else {
                            setCurrentPasswordError(" ");
                            setNewPasswordError(" ");
                            setConfirmPasswordError(" ");
                        }
                    } else {
                        setCurrentPasswordError(" ");
                        setNewPasswordError(" ");
                        setConfirmPasswordError(" ");
                    }
                    return;
                }
            }
        } catch (error) {
            const parsedError = parseError(error);
            showSnackbar(parsedError, "error");
            setCurrentPasswordError(" ");
            setNewPasswordError(" ");
            setConfirmPasswordError(" ");
        }
    };

    return (
        <Box sx={{
            paddingTop: { xs: '24px', md: '48px' },
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
        }}>

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: {xs: 'column', md: 'row'},
                        gap: 3,
                        height: {md: '80px'}
                    }}>
                        <Box sx={{flex: 1}}>
                            <StyledTextField
                                label={t('passwordSecurity.currentPassword')}
                                type={showCurrentPassword ? "text" : "password"}
                                value={currentPassword}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                    setCurrentPassword(e.target.value);
                                    setCurrentPasswordError("");
                                    setNewPasswordError("");
                                    setConfirmPasswordError("");
                                }}
                                fullWidth
                                variant="outlined"
                                error={!!currentPasswordError}
                                helperText={currentPasswordError}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <IconButton
                                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                                    edge="end"
                                                    sx={{color: 'primary.main'}}
                                                >
                                                    {showCurrentPassword ? <VisibilityOff/> : <Visibility/>}
                                                </IconButton>
                                                <FieldHelpSuffix
                                                    title={t('passwordSecurity.help.currentPassword')}
                                                    ariaLabel={t('passwordSecurity.help.currentPassword') as string}
                                                />
                                            </Box>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>

                        <Box sx={{flex: 1}}>
                            <StyledTextField
                                label={t('passwordSecurity.newPassword')}
                                type={showNewPassword ? "text" : "password"}
                                value={newPassword}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                    setNewPassword(e.target.value);
                                    setCurrentPasswordError("");
                                    setNewPasswordError("");
                                    setConfirmPasswordError("");
                                }}
                                onBlur={() => {
                                    const error = validatePasswordUtil(newPassword, t);
                                    setNewPasswordError(error || "");
                                }}
                                fullWidth
                                variant="outlined"
                                error={!!newPasswordError}
                                helperText={newPasswordError}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <IconButton
                                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                                    edge="end"
                                                    sx={{color: 'primary.main'}}
                                                >
                                                    {showNewPassword ? <VisibilityOff/> : <Visibility/>}
                                                </IconButton>
                                                <FieldHelpSuffix
                                                    title={t('passwordSecurity.help.newPassword')}
                                                    ariaLabel={t('passwordSecurity.help.newPassword') as string}
                                                />
                                            </Box>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>

                        <Box sx={{flex: 1}}>
                            <StyledTextField
                                label={t('passwordSecurity.confirmPassword')}
                                type={showConfirmPassword ? "text" : "password"}
                                value={confirmPassword}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                    setConfirmPassword(e.target.value);
                                    setCurrentPasswordError("");
                                    setNewPasswordError("");
                                    setConfirmPasswordError("");
                                }}
                                onBlur={() => {
                                    if (newPassword !== confirmPassword && confirmPassword !== "") {
                                        setConfirmPasswordError(t('passwordSecurity.passwordsNotMatch') as string);
                                    } else {
                                        setConfirmPasswordError("");
                                    }
                                }}
                                fullWidth
                                variant="outlined"
                                error={!!confirmPasswordError || (newPassword !== confirmPassword && confirmPassword !== "")}
                                helperText={
                                    confirmPasswordError || (newPassword !== confirmPassword && confirmPassword !== ""
                                        ? t('passwordSecurity.passwordsNotMatch')
                                        : "")
                                }
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <IconButton
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    edge="end"
                                                    sx={{color: 'primary.main'}}
                                                >
                                                    {showConfirmPassword ? <VisibilityOff/> : <Visibility/>}
                                                </IconButton>
                                                <FieldHelpSuffix
                                                    title={t('passwordSecurity.help.confirmPassword')}
                                                    ariaLabel={t('passwordSecurity.help.confirmPassword') as string}
                                                />
                                            </Box>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            <ButtonGroup>
                <CancelButton onClick={() => navigate(-1)}>
                    {t('common.cancel')}
                </CancelButton>
                <ActionButton
                    onClick={handleSaveChanges}
                    disabled={!isFormChanged}
                >
                    {t('common.saveChanges')}
                </ActionButton>
            </ButtonGroup>
        </Box>
    );
};

export default PasswordSecurity;
